import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import OutsideClickHandler from 'react-outside-click-handler';
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrow-down.svg';
import { ReactComponent as CheckmarkIcon } from '../../../assets/icons/Check.svg';
import './MultiSelect.scss';

interface SelectOption {
  key: any;
  value: any;
}

interface MultiSelectProps {
  options: SelectOption[];
  selectedOptions: any[];
  onChange: (option: SelectOption, name: string) => void;
  placeholder?: string;
  name: string;
  isError: boolean;
  errorMessage?: string;
  disabled?: boolean;
  title?: string;
  maxHeight?: string;
  useMenuPortal?: boolean;
  openMenuToTop?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedOptions,
  onChange,
  placeholder,
  name,
  disabled,
  title,
  maxHeight,
  useMenuPortal = false,
  openMenuToTop = false,
}) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const onOptionSelect = (option: SelectOption) => {
    onChange(option, name);
  };

  const selectedOptionsValues = useMemo(
    () => selectedOptions.map((option) => option.value),
    [selectedOptions]
  );

  const updateMenuPosition = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const resolvedMaxHeight = Number.parseInt(maxHeight ?? '300', 10) || 300;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const shouldOpenUp =
      openMenuToTop || (spaceBelow < Math.min(resolvedMaxHeight, 160) && spaceAbove > spaceBelow);

    if (shouldOpenUp) {
      setMenuStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        bottom: window.innerHeight - rect.top + 8,
        maxHeight: `${Math.min(resolvedMaxHeight, spaceAbove)}px`,
        zIndex: 2100,
      });
      return;
    }

    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      maxHeight: `${Math.min(resolvedMaxHeight, spaceBelow)}px`,
      zIndex: 2100,
    });
  }, [maxHeight, openMenuToTop]);

  useEffect(() => {
    if (!useMenuPortal || !isDropdownVisible) return;

    updateMenuPosition();

    const handleScrollOrResize = () => updateMenuPosition();
    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);

    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [useMenuPortal, isDropdownVisible, updateMenuPosition]);

  useEffect(() => {
    if (!useMenuPortal || !isDropdownVisible) return;

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }

      setIsDropdownVisible(false);
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [useMenuPortal, isDropdownVisible]);

  const handleToggleDropdown = () => {
    if (!disabled) setIsDropdownVisible((prev) => !prev);
  };

  const inputSection = (
    <div
      className={`select-input-container ${disabled ? 'disabled-select' : ''}`}
      onClick={handleToggleDropdown}
    >
      {title && <div className='title'>{title}</div>}
      <div className='selected-options-list'>
        {selectedOptionsValues && selectedOptionsValues.length ? (
          <div className='selected-list'>{selectedOptionsValues.join(', ')}</div>
        ) : (
          <div className='placeholder'>{placeholder}</div>
        )}
      </div>
      <ArrowDownIcon
        className={`multi-select-arrow ${isDropdownVisible && 'rotated'}`}
      />
    </div>
  );

  const dropdownMenu = isDropdownVisible ? (
    <div
      ref={dropdownRef}
      className={`select-dropdown${useMenuPortal ? ' select-dropdown--portal' : ''}`}
      style={
        useMenuPortal
          ? menuStyle
          : {
              maxHeight,
            }
      }
    >
      {options.map((option) => (
        <div
          key={option.key}
          className='select-option'
          onClick={() => onOptionSelect(option)}
        >
          {option.value}
          {selectedOptions?.find((el) => el.key === option.key) && (
            <CheckmarkIcon className='checkmark-icon' />
          )}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <div className='multi-select-container' ref={containerRef}>
      {useMenuPortal ? (
        inputSection
      ) : (
        <OutsideClickHandler
          onOutsideClick={() => {
            if (!disabled) setIsDropdownVisible(false);
          }}
        >
          {inputSection}
          {dropdownMenu}
        </OutsideClickHandler>
      )}
      {useMenuPortal &&
        dropdownMenu &&
        createPortal(dropdownMenu, document.body)}
    </div>
  );
};

export default MultiSelect;
